import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useVendors } from '../../hooks/useVendors'
import { parseError } from '../../lib/errors'
import {
  type FailureReport,
  FindingStatus,
  FixKind,
  useApplyFailureFixes,
  useDismissFailureFinding,
  useRefreshFailureAnalysis,
} from '../../models/indicationFailure'
import { FailureFindingRow, type FindingDraft } from './FailureFindingRow'

async function toastOnError(run: () => Promise<unknown>) {
  try {
    await run()
  } catch (error) {
    toast.error(parseError(error))
  }
}

export function FailureAnalysisDialog({
  applicationId,
  report,
  isAnalysing,
  open,
  onOpenChange,
}: {
  applicationId: string
  report: FailureReport
  /** A fresh report is still running, so the findings below are the previous set. */
  isAnalysing?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  // Fixable first — the banner promises N fixable, so don't bury them.
  const openFindings = report.findings
    .filter((f) => f.status === FindingStatus.Open)
    .sort((a, b) => Number(b.isFixable) - Number(a.isFixable))
  const [drafts, setDrafts] = useState<Record<string, FindingDraft | undefined>>(() =>
    Object.fromEntries(
      openFindings.map((f) => [
        f.id,
        // Pre-ticked only when we already hold a value — never when a human must still type one.
        { selected: f.isFixable && !!f.proposedValue, value: f.proposedValue },
      ])
    )
  )

  const apply = useApplyFailureFixes(applicationId)
  const refresh = useRefreshFailureAnalysis(applicationId)
  const dismiss = useDismissFailureFinding(applicationId)
  const { vendorLabel } = useVendors()

  const chosen = openFindings.filter((f) => {
    const draft = drafts[f.id]
    // isFixable is the model's own answer to "can apply_finding write this?"
    if (!draft?.selected || !f.isFixable) return false
    // A field edit with nothing typed has no value to send — hold it back rather than fail server-side.
    return f.fixKind !== FixKind.FieldEdit || !!draft.value.trim()
  })

  function applyChosen() {
    return toastOnError(async () => {
      const result = await apply.mutateAsync(
        chosen.map((f) => ({ findingId: f.id, value: drafts[f.id]?.value || undefined }))
      )
      if (result.failed.length) {
        const plural = result.failed.length > 1 ? 'es' : ''
        toast.error(`${result.failed.length} fix${plural} could not be applied`, {
          description: result.failed.map((failure) => failure.error).join(' '),
        })
      }
      if (result.requote.dispatched.length) {
        toast.success(`Requoting ${result.requote.dispatched.map(vendorLabel).join(', ')}`)
      } else if (result.applied.length) {
        // Applied but nothing to requote: say so, or the dialog just shuts with no word.
        toast.success(`${result.applied.length} fix${result.applied.length > 1 ? 'es' : ''} applied`)
      }
      result.requote.notices?.forEach((notice) => toast.warning(notice))
      if (!result.failed.length) onOpenChange(false)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Why the carriers did not quote</DialogTitle>
          {report.summary && (
            // Blank lines are paragraph breaks; HTML would otherwise collapse them into a wall.
            <DialogDescription className="flex flex-col gap-2">
              {report.summary
                .split(/\n\s*\n/)
                .map((para) => para.trim())
                .filter(Boolean)
                .map((para) => (
                  <span key={para}>{para}</span>
                ))}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto">
          {openFindings.map((f) => (
            <FailureFindingRow
              key={f.id}
              finding={f}
              draft={drafts[f.id] ?? { selected: false, value: '' }}
              onChange={(draft) => setDrafts((prev) => ({ ...prev, [f.id]: draft }))}
              onDismiss={() => toastOnError(() => dismiss.mutateAsync(f.id))}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            loading={isAnalysing || refresh.isPending ? 'Re-analysing…' : undefined}
            onClick={() => toastOnError(() => refresh.mutateAsync())}
          >
            Re-run analysis
          </Button>
          <Button
            disabled={!chosen.length}
            loading={apply.isPending ? 'Applying…' : undefined}
            onClick={applyChosen}
          >
            {chosen.length
              ? `Apply ${chosen.length} fix${chosen.length > 1 ? 'es' : ''} and requote`
              : 'Apply and requote'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
